import React, { useCallback, useState } from "react";

import { Button, Col, Row } from "react-bootstrap";

import { useForm } from "react-hook-form";

import {
  FinanceContactFragment,
  FinanceContactInput,
  ParticipantTypes,
  useUpdateFinanceContactDetailsMutation,
} from "@api/graphql";
import { PenWriteEditIcon } from "@assets/icons";
import { IconLink } from "@components/IconLink";
import { ToastMessage } from "@components/ToastMessage";
import { CustomAccordion } from "@components/accordion/CustomAccordion";
import { financeContactErrorResolver } from "@components/account_profile/account_details/FinanceContactErrorResolver";
import { FieldFormatter } from "@components/detail_drawer/FieldFormatter";
import { DetailCell } from "@components/resale_submission/DetailCell";
import { UserDetailsInput } from "@pages/register/steps/RegisterFormTypes";
import { FinanceContactInformationForm } from "@pages/register/steps/UserDetails/FinanceContactInformationForm";

interface FinanceContactProps {
  financeContact: FinanceContactFragment;
  participantTypes: (ParticipantTypes | undefined)[];
  participantId?: number;
  participant_is_reporter?: boolean;
  accountStatus?: string;
}

const FINANCE_CONTACT_DETAILS_FORMATTERS: FieldFormatter<FinanceContactFragment>[] = [
  { title: "First Name", format: (financeContact) => financeContact.first_name || "-" },
  { title: "Last Name", format: (financeContact) => financeContact.last_name || "-" },
  { title: "Pronouns", format: (financeContact) => financeContact.pronouns || "-" },
  { title: "Email", format: (financeContact) => financeContact.finance_email || "-" },
  { title: "Mobile number", format: (financeContact) => financeContact.finance_phone || "-" },
];

const FinanceContactAccordion: React.FC<FinanceContactProps> = ({
  financeContact,
  participantTypes,
  participantId,
  participant_is_reporter,
  accountStatus,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [currentFinanceContact, setCurrentFinanceContact] = useState<FinanceContactFragment>(financeContact);
  const [updateFinanceContactDetailsMutation] = useUpdateFinanceContactDetailsMutation();

  const {
    control,
    formState: { errors, isValid },
    getValues,
  } = useForm<UserDetailsInput>({
    mode: "onChange",
    reValidateMode: "onChange",
    resolver: (data) =>
      financeContactErrorResolver(
        data,
        accountStatus,
        participantTypes?.filter((type): type is ParticipantTypes => type !== undefined),
      ),
    defaultValues: { finance_contact: currentFinanceContact },
  });

  const submitHandler = useCallback(async () => {
    const data = getValues();
    if (!participantId) {
      setErrorMessage("Participant ID is missing, cannot update Invoice contact information.");
      setToastMessage("Participant ID is missing, cannot update Invoice contact information.");
      setShowToast(true);
      return;
    }
    try {
      const response = await updateFinanceContactDetailsMutation({
        variables: {
          participantId: participantId,
          financeContactInput: { ...cleanInput(data.finance_contact) },
        },
      });

      const updateResult = response.data?.updateFinanceContactDetails;
      if (updateResult?.success) {
        setCurrentFinanceContact(updateResult.finance_contact as FinanceContactFragment);
        setIsEditing(false);
        setToastMessage(updateResult.message || "Invoice contact information updated successfully!");
      } else {
        setErrorMessage(updateResult?.message || "Failed to update Invoice contact information");
        setToastMessage(updateResult?.message || "Failed to update Invoice contact information");
      }
    } catch (error) {
      console.error(error);
      setErrorMessage("An error occurred while updating Invoice contact information");
      setToastMessage("An error occurred while updating Invoice contact information");
    } finally {
      setShowToast(true);
    }
  }, [getValues, participantId, updateFinanceContactDetailsMutation]);

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  const shouldShow =
    participantTypes.includes(ParticipantTypes.CMO) ||
    (participantTypes.includes(ParticipantTypes.ART_CENTRE) && participant_is_reporter) ||
    (participantTypes.includes(ParticipantTypes.AGENT) && participant_is_reporter) ||
    participantTypes.includes(ParticipantTypes.AMP);

  if (!shouldShow) {
    return null;
  }

  return (
    <>
      <ToastMessage message={toastMessage} show={showToast} setShow={setShowToast} type="info" />
      <CustomAccordion
        accordionKey="FinanceContact"
        contrastOpenStyle={false}
        header={<h5 className="mb-0">Invoice contact information</h5>}
        defaultActiveKey="FinanceContact"
        customHeaderStyle="py-4"
        preventCloseOnContentClick
        showBottomLine
      >
        <Row>
          <Col md={11}>
            {isEditing ? (
              <FinanceContactInformationForm
                control={control}
                errors={errors}
                accountStatus={accountStatus}
                participantTypes={participantTypes?.filter((type): type is ParticipantTypes => type !== undefined)}
              />
            ) : (
              <Row className="gap-4">
                {FINANCE_CONTACT_DETAILS_FORMATTERS.map(({ title, format }) => (
                  <Col md={5} className="d-flex flex-column" key={title}>
                    <DetailCell title={title} value={format(currentFinanceContact)} />
                  </Col>
                ))}
              </Row>
            )}
          </Col>
          {!isEditing && (
            <Col md={1} className="d-flex justify-content-end">
              <Col xs="auto">
                <IconLink to="#" src={PenWriteEditIcon} text="Edit" className="me-2" onClick={handleEditToggle} />
              </Col>
            </Col>
          )}
        </Row>
        {isEditing && (
          <Row className="mt-3">
            {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}

            <Col className="d-flex justify-content-end">
              <Button variant="secondary" onClick={handleCancel} className="me-2">
                Cancel
              </Button>
              <Button variant="primary" disabled={!isValid} onClick={submitHandler}>
                Save
              </Button>
            </Col>
          </Row>
        )}
      </CustomAccordion>
    </>
  );
};

const cleanInput = (input: Partial<FinanceContactFragment>): FinanceContactInput => {
  return {
    first_name: input.first_name,
    last_name: input.last_name,
    pronouns: input.pronouns,
    finance_email: input.finance_email,
    finance_phone: input.finance_phone,
  };
};

export default FinanceContactAccordion;
